import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, withModifiers as _withModifiers, renderSlot as _renderSlot, resolveDynamicComponent as _resolveDynamicComponent, createBlock as _createBlock, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { key: 0 }
const _hoisted_2 = { class: "input-icon--left" }
const _hoisted_3 = { class: "base-input--label" }
const _hoisted_4 = ["for"]
const _hoisted_5 = ["innerHTML"]
const _hoisted_6 = { class: "input-icon--right" }

import {
  reactive,
  computed,
  ref,
  onMounted, watch, useId,
} from 'vue';

import { library } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faCheck, faSpinner, faCalendarAlt } from '@fortawesome/pro-light-svg-icons';
import TextField from '@/components/inputs/TextField.vue';
import TextareaField from '@/components/inputs/TextareaField.vue';
import NumberField from '@/components/inputs/NumberField.vue';
import DateField from '@/components/inputs/DateField.vue';


export default /*@__PURE__*/_defineComponent({
  __name: 'InputField',
  props: {
  type: {
    type: String,
    required: false,
    default: 'text',
    validator(val) {
      return ['text', 'number', 'textarea', 'date'].indexOf(val) >= 0;
    },
  },
  modelValue: {},
  name: String,
  label: {
    type: String,
    required: true,
  },
  readonly: Boolean,
  disabled: Boolean,
  options: {
    type: Array,
    required: false,
    default: () => [],
  },
  error: {
    type: [String, Array],
    required: false,
    default: () => '',
  },
  minDate: [String, Number, Date],
  maxDate: [String, Number, Date],
  minLength: {
    type: Number,
    required: false,
    default: () => null,
  },
  maxLength: {
    type: Number,
    required: false,
    default: () => null,
  },
  min: {
    type: Number,
    required: false,
    default: () => null,
  },
  max: {
    type: Number,
    required: false,
    default: () => null,
  },
  onlyNumeric: {
    type: Boolean,
    required: false,
    default: () => false,
  },
  errorNumeric: {
    type: String,
    required: false,
    default: () => null,
  },
  showLoader: {
    type: Boolean,
    required: false,
    default: () => false,
  },
  showValidCheck: {
    type: Boolean,
    required: false,
    default: () => false,
  },
  disableWeekend: {
    type: Boolean,
    required: false,
    default: () => false,
  },
  autocomplete: {
    type: String,
    required: false,
    default: () => 'on',
  },
  iconRight: {
    type: String,
    required: false,
    default: () => null,
  },
  iconLeft: {
    type: String,
    required: false,
    default: () => null,
  },
  borderless: {
    type: Boolean,
    required: false,
    default: () => false,
  },
  rounded: {
    type: Boolean,
    required: false,
    default: () => true,
  },
  hideLabel: {
    type: Boolean,
    required: false,
    default: () => false,
  },
  preventPaste: {
    type: Boolean,
    required: false,
    default: () => false,
  },
  isRequired: {
    type: Boolean,
    required: false,
    default: () => false,
  },
},
  emits: [
  'update:modelValue',
  'input',
  'keyup',
  'focusin',
  'focusout',
],
  setup(__props, { emit: __emit }) {

library.add(faCheck);
library.add(faSpinner);
library.add(faCalendarAlt);

const emit = __emit;

const props = __props;

const componentTypes: Record<string, any> = {
  text: TextField,
  textarea: TextareaField,
  number: NumberField,
  date: DateField,
};

const uniqId = useId();
const componentId = `${uniqId}-input`;
const errorId = `${uniqId}-error`;

const component = reactive({
  id: componentId,
  currentComponent: computed(() => componentTypes[props.type]),
  error: props.error || '',
});

const input = ref();
const focused = ref(false);

const onFocusIn = (event: FocusEvent) => {
  focused.value = true;
  emit('focusin', event);
};

const onFocusOut = (event: FocusEvent) => {
  focused.value = false;
  emit('focusout', event);
};

const onClickLabel = () => {
  if (input.value) {
    if (input.value.onFocus) {
      input.value.onFocus();
    } else if (input.value.$el) {
      input.value.$el.focus();
    }
  }
};

const value = computed({
  get(): unknown {
    return props.modelValue;
  },
  set(newValue: unknown): void {
    emit('update:modelValue', newValue);
  },
});

const hasError = computed(() => {
  const compError = `${Array.isArray(component.error) && component.error[0] ? component.error[0] : component.error}`.trim();
  const $error = compError.length ? compError : (props.error || '');

  return $error !== null
    ? $error.length > 0
    : false;
});

const errorMessage = computed(() => {
  if (hasError.value) {
    const compError = `${Array.isArray(component.error) && component.error[0] ? component.error[0] : component.error}`.trim();
    const $error = compError.length ? compError : props.error;

    return Array.isArray($error)
      ? $error[0]
      : $error;
  }

  return '';
});

const onPaste = (e: Event) => {
  if (props.preventPaste) {
    e.preventDefault();
  }
};

const emitInput = ($value: unknown) => {
  emit('input', $value);
};

const onInput = ($value: string | number) => {
  let formattedVal = $value;

  if (typeof formattedVal === 'string') {
    if (props.onlyNumeric) {
      formattedVal = formattedVal.replace(/\D/g, '');
    }

    if (props.minLength !== null && formattedVal.length < props.minLength) {
      component.error = `Mindestens ${props.minLength} Zeichen`;
      if (props.onlyNumeric) {
        component.error = props.errorNumeric || `Zeichenfolge aus mindestens ${props.minLength} Zahlen`;
      }
    } else if (props.maxLength !== null && formattedVal.length > props.maxLength) {
      formattedVal = formattedVal.substr(0, props.maxLength);
      if (input.value) {
        input.value.$el.value = formattedVal;
      }
    }
    if (props.minLength !== null && formattedVal.length >= props.minLength) {
      component.error = [];
    }
  }

  value.value = formattedVal;
  emitInput(formattedVal);
};

const onBlur = () => {
  let formattedVal = value.value;

  if (typeof formattedVal === 'string') {
    if (props.onlyNumeric) {
      let stringVal = formattedVal as string;

      stringVal = stringVal.replace(/\D/g, '');

      const numberValue = parseInt(stringVal, 10);

      if (input.value) {
        if (props.min !== null && numberValue < props.min) {
          formattedVal = `${props.min}`;
        }

        if (props.max !== null && numberValue > props.max) {
          formattedVal = `${props.max}`;
        }

        input.value.$el.value = formattedVal;
      } else if (props.min !== null) {
        input.value.$el.value = formattedVal;
      }
    }
  } else if (input.value) {
    const numberValue = formattedVal as number;

    if (props.min !== null && numberValue < props.min) {
      formattedVal = props.min;
    }

    if (props.max !== null && numberValue > props.max) {
      formattedVal = props.max;
    }

    input.value.$el.value = formattedVal;
  }

  value.value = formattedVal;
  emitInput(formattedVal);
};

const filled = computed(() => `${typeof value.value === 'number' ? value.value : value.value || ''}`.trim() !== '');

onMounted(() => {
  emitInput(props.modelValue);
});

watch(() => props.max, onBlur);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["base-input-field", {
      'invalid': hasError.value,
      'readonly': __props.readonly,
      'valid': !__props.showLoader && !hasError.value && __props.showValidCheck,
      'loading': __props.showLoader,
      'icon-right': __props.iconRight,
      'icon-left': __props.iconLeft,
      'borderless': __props.borderless,
      'rounded': __props.rounded,
      'filled': filled.value,
      'focused': focused.value,
      'label-hidden': __props.hideLabel,
    }]),
    onClick: onClickLabel
  }, [
    (__props.iconLeft)
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
          _createElementVNode("span", _hoisted_2, [
            _createVNode(_unref(FontAwesomeIcon), { icon: __props.iconLeft }, null, 8, ["icon"])
          ])
        ]))
      : _createCommentVNode("", true),
    _createElementVNode("div", {
      class: _normalizeClass(["col-main", { 'border-right': __props.type === 'date' }])
    }, [
      _createElementVNode("div", null, [
        _createElementVNode("div", _hoisted_3, [
          _createElementVNode("label", {
            for: component.id,
            onClick: _withModifiers(onClickLabel, ["prevent"])
          }, _toDisplayString(__props.label), 9, _hoisted_4),
          _renderSlot(_ctx.$slots, "tooltip")
        ]),
        (_openBlock(), _createBlock(_resolveDynamicComponent(component.currentComponent), {
          value: value.value,
          name: __props.name,
          id: component.id,
          readonly: __props.readonly,
          disabled: __props.disabled,
          "min-date": __props.minDate,
          "max-date": __props.maxDate,
          ref_key: "input",
          ref: input,
          "disable-weekend": __props.disableWeekend,
          autocomplete: __props.autocomplete,
          "aria-required": __props.isRequired ? 'true' : undefined,
          "aria-invalid": hasError.value ? 'true' : undefined,
          "aria-disabled": __props.disabled ? 'true' : undefined,
          "aria-readonly": __props.readonly ? 'true' : undefined,
          "aria-describedby": hasError.value ? errorId : undefined,
          onKeypress: _cache[0] || (_cache[0] = ($event: any) => (component.error = [])),
          onInput: onInput,
          onBlur: onBlur,
          onFocusin: onFocusIn,
          onFocusout: onFocusOut,
          onKeyup: _cache[1] || (_cache[1] = ($event: any) => (emit('keyup', $event))),
          onPaste: onPaste,
          onDrop: onPaste
        }, null, 40, ["value", "name", "id", "readonly", "disabled", "min-date", "max-date", "disable-weekend", "autocomplete", "aria-required", "aria-invalid", "aria-disabled", "aria-readonly", "aria-describedby"])),
        (hasError.value)
          ? (_openBlock(), _createElementBlock("div", {
              key: 0,
              id: errorId,
              class: "base-input-field__error-message fs-small",
              innerHTML: errorMessage.value
            }, null, 8, _hoisted_5))
          : _createCommentVNode("", true)
      ])
    ], 2),
    ((!__props.showLoader && !hasError.value && __props.showValidCheck) || __props.showLoader || __props.iconRight)
      ? (_openBlock(), _createElementBlock("div", {
          key: 1,
          onClick: onClickLabel
        }, [
          _createElementVNode("span", _hoisted_6, [
            (!__props.showLoader && !hasError.value && __props.showValidCheck)
              ? (_openBlock(), _createBlock(_unref(FontAwesomeIcon), {
                  key: 0,
                  icon: "fa-light fa-check"
                }))
              : (__props.showLoader)
                ? (_openBlock(), _createBlock(_unref(FontAwesomeIcon), {
                    key: 1,
                    icon: "fa-light fa-spinner fa-pulse"
                  }))
                : (__props.iconRight)
                  ? (_openBlock(), _createBlock(_unref(FontAwesomeIcon), {
                      key: 2,
                      icon: __props.iconRight
                    }, null, 8, ["icon"]))
                  : _createCommentVNode("", true)
          ])
        ]))
      : _createCommentVNode("", true)
  ], 2))
}
}

})